import { Message } from 'element-ui'

/**
 * HTTP请求工具类
 * 基于fetch API封装，支持baseUrl配置和常见HTTP方法
 * 支持请求熔断机制，避免重复请求问题
 */
class HttpClient {
  constructor(config = {}) {
    this.baseURL = config.baseURL || ''
    this.timeout = config.timeout || 3 * 60 * 1000
    this.headers = {
      'Content-Type': 'application/json',
      ...config.headers
    }

    // 请求拦截器
    this.requestInterceptors = []
    // 响应拦截器
    this.responseInterceptors = []

    // 熔断机制：存储正在进行的请求
    // 格式：{ code: { controller: AbortController, promise: Promise } }
    this.pendingRequests = new Map()
  }

  /**
   * 添加请求拦截器
   * @param {Function} fulfilled - 成功回调
   * @param {Function} rejected - 失败回调
   */
  addRequestInterceptor(fulfilled, rejected) {
    this.requestInterceptors.push({ fulfilled, rejected })
  }

  /**
   * 添加响应拦截器
   * @param {Function} fulfilled - 成功回调
   * @param {Function} rejected - 失败回调
   */
  addResponseInterceptor(fulfilled, rejected) {
    this.responseInterceptors.push({ fulfilled, rejected })
  }

  /**
   * 取消指定code的请求
   * @param {string} code - 请求的唯一标识
   */
  cancelRequest(code) {
    if (this.pendingRequests.has(code)) {
      const { controller } = this.pendingRequests.get(code)
      controller.abort()
      this.pendingRequests.delete(code)
      console.log(`已取消请求: ${code}`)
    }
  }

  /**
   * 清理完成的请求
   * @param {string} code - 请求的唯一标识
   */
  cleanupRequest(code) {
    if (this.pendingRequests.has(code)) {
      this.pendingRequests.delete(code)
      console.log(`清理请求: ${code}`)
    }
  }

  /**
   * 检查是否有正在进行的请求
   * @param {string} code - 请求的唯一标识
   * @returns {boolean} 是否有正在进行的请求
   */
  hasPendingRequest(code) {
    return this.pendingRequests.has(code)
  }

  /**
   * 获取所有正在进行的请求代码
   * @returns {Array<string>} 正在进行的请求代码列表
   */
  getPendingRequestCodes() {
    return Array.from(this.pendingRequests.keys())
  }

  /**
   * 处理请求拦截器
   * @param {Object} config - 请求配置
   * @returns {Object} 处理后的配置
   */
  async processRequestInterceptors(config) {
    let processedConfig = config

    for (const interceptor of this.requestInterceptors) {
      try {
        if (interceptor.fulfilled) {
          processedConfig = await interceptor.fulfilled(processedConfig)
        }
      } catch (error) {
        if (interceptor.rejected) {
          return interceptor.rejected(error)
        }
        throw error
      }
    }

    return processedConfig
  }

  /**
   * 处理响应拦截器
   * @param {Response} response - 响应对象
   * @returns {Response} 处理后的响应
   */
  async processResponseInterceptors(response) {
    let processedResponse = response

    for (const interceptor of this.responseInterceptors) {
      try {
        if (interceptor.fulfilled) {
          processedResponse = await interceptor.fulfilled(processedResponse)
        }
      } catch (error) {
        if (interceptor.rejected) {
          return interceptor.rejected(error)
        }
        throw error
      }
    }

    return processedResponse
  }

  /**
   * 构建完整URL
   * @param {string} url - 请求路径
   * @returns {string} 完整URL
   */
  buildURL(url) {
    if (!url) url = ''
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }

    const baseURL = this.baseURL.endsWith('/')
      ? this.baseURL.slice(0, -1)
      : this.baseURL
    const path = url.startsWith('/') ? url : `/${url}`

    return `${baseURL}${path}`
  }

  /**
   * 处理查询参数
   * @param {Object} params - 查询参数对象
   * @returns {string} 查询字符串
   */
  buildQueryString(params) {
    if (!params || Object.keys(params).length === 0) {
      return ''
    }

    const searchParams = new URLSearchParams()
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        searchParams.append(key, params[key])
      }
    })

    return searchParams.toString()
  }

  /**
   * 解析JSON并处理长整型精度问题
   * @param {string} jsonString - JSON字符串
   * @returns {Object} 解析后的对象
   */
  parseJSONWithBigInt(jsonString) {
    // 正则表达式匹配可能的长整型数字（超过JavaScript安全整数范围）
    // 匹配条件：纯数字且长度大于15位，或者数值超过Number.MAX_SAFE_INTEGER
    // 匹配三种情况：
    // 1. 对象属性值: "key": 123456789012345678
    // 2. 数组元素: [123456789012345678, ...]
    // 3. 数组中的元素: ,123456789012345678,
    const longIntRegex = /(:\s*|[\[\,]\s*)(-?\d{16,})(?=\s*[\,\]\}])/g

    // 将可能的长整型数字转换为字符串
    const processedJsonString = jsonString.replace(
      longIntRegex,
      (match, prefix, number) => {
        const numValue = BigInt(number)
        // 如果数字超过JavaScript安全整数范围，则转换为字符串
        if (
          numValue > Number.MAX_SAFE_INTEGER ||
          numValue < Number.MIN_SAFE_INTEGER
        ) {
          return `${prefix}"${number}"`
        }
        return match
      }
    )

    try {
      return JSON.parse(processedJsonString)
    } catch (error) {
      console.warn('JSON解析失败，使用原始解析:', error)
      return JSON.parse(jsonString)
    }
  }

  /**
   * 核心请求方法
   * @param {Object} config - 请求配置
   * @returns {Promise} 请求Promise
   */
  async request(config) {
    try {
      // 处理请求拦截器
      const processedConfig = await this.processRequestInterceptors(config)

      const {
        url,
        method = 'GET',
        data,
        params,
        headers = {},
        timeout = this.timeout,
        code, // 熔断机制的唯一标识
        ...otherOptions
      } = processedConfig

      // 如果提供了code，先取消之前的请求
      if (code) {
        this.cancelRequest(code)
      }

      // 构建完整URL
      let fullURL = this.buildURL(url)

      // 处理查询参数
      if (params) {
        const queryString = this.buildQueryString(params)
        if (queryString) {
          fullURL += (fullURL.includes('?') ? '&' : '?') + queryString
        }
      }

      // 合并headers
      const mergedHeaders = {
        ...this.headers,
        ...headers
      }

      // 构建fetch选项
      const fetchOptions = {
        method: method.toUpperCase(),
        headers: mergedHeaders,
        ...otherOptions
      }

      // 处理请求体
      if (data && ['POST', 'PUT', 'PATCH'].includes(fetchOptions.method)) {
        if (mergedHeaders['Content-Type'] === 'application/json') {
          fetchOptions.body = JSON.stringify(data)
        } else if (data instanceof FormData) {
          fetchOptions.body = data
          // FormData会自动设置Content-Type，需要删除手动设置的
          delete fetchOptions.headers['Content-Type']
        } else {
          fetchOptions.body = data
        }
      }

      // 创建超时和熔断控制
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), timeout)
      fetchOptions.signal = controller.signal

      // 如果提供了code，将请求信息存储到pendingRequests中
      let requestPromise
      if (code) {
        requestPromise = this._performRequest(
          fullURL,
          fetchOptions,
          timeoutId,
          code
        )
        this.pendingRequests.set(code, {
          controller,
          promise: requestPromise,
          url: fullURL,
          startTime: Date.now()
        })
      } else {
        requestPromise = this._performRequest(fullURL, fetchOptions, timeoutId)
      }

      return await requestPromise
    } catch (error) {
      if (error.message === '请求被取消') return {}
      Message.error(error.message || '服务异常')
      return {}
    }
  }

  /**
   * 执行实际的请求
   * @param {string} fullURL - 完整URL
   * @param {Object} fetchOptions - fetch选项
   * @param {number} timeoutId - 超时ID
   * @param {string} code - 请求代码（可选）
   * @returns {Promise} 请求结果
   */
  async _performRequest(fullURL, fetchOptions, timeoutId, code) {
    try {
      // 发送请求
      const response = await fetch(fullURL, fetchOptions)
      clearTimeout(timeoutId)

      // 如果有code，清理请求缓存
      if (code) {
        this.cleanupRequest(code)
      }

      // 处理响应拦截器
      const processedResponse = await this.processResponseInterceptors(response)

      // 检查响应状态
      if (!processedResponse.ok) {
        throw new Error(
          `HTTP Error: ${processedResponse.status} ${processedResponse.statusText}`
        )
      }

      // 解析响应数据
      const contentType = processedResponse.headers.get('content-type')
      let responseData

      if (contentType && contentType.includes('application/json')) {
        // 获取原始文本，然后使用自定义解析器处理长整型
        const responseText = await processedResponse.text()
        responseData = this.parseJSONWithBigInt(responseText)
      } else if (contentType && contentType.includes('text/')) {
        responseData = await processedResponse.text()
      } else {
        responseData = await processedResponse.blob()
      }

      if (responseData.code !== 200) {
        Message.error(responseData.message || '服务异常')
      }
      return responseData
    } catch (error) {
      clearTimeout(timeoutId)

      // 如果有code，清理请求缓存
      if (code) {
        this.cleanupRequest(code)
      }

      // 如果是取消请求，不显示错误信息
      if (error.name === 'AbortError') {
        console.log(`请求被取消: ${code || fullURL}`)
        throw new Error('请求被取消')
      }

      Message.error(error.message || '服务异常')
      throw error
    }
  }

  /**
   * GET请求
   * @param {string} url - 请求URL
   * @param {Object} params - 查询参数
   * @param {Object} config - 请求配置
   * @returns {Promise} 请求Promise
   */
  get(url, params = {}, config = {}) {
    return this.request({
      ...config,
      params: JSON.parse(JSON.stringify(params)),
      url,
      method: 'GET'
    })
  }

  /**
   * POST请求
   * @param {string} url - 请求URL
   * @param {*} data - 请求数据
   * @param {Object} config - 请求配置
   * @returns {Promise} 请求Promise
   */
  post(url, data, config = {}) {
    return this.request({
      ...config,
      url,
      method: 'POST',
      data
    })
  }

  /**
   * PUT请求
   * @param {string} url - 请求URL
   * @param {*} data - 请求数据
   * @param {Object} config - 请求配置
   * @returns {Promise} 请求Promise
   */
  put(url, data, config = {}) {
    return this.request({
      ...config,
      url,
      method: 'PUT',
      data
    })
  }

  /**
   * DELETE请求
   * @param {string} url - 请求URL
   * @param {Object} config - 请求配置
   * @returns {Promise} 请求Promise
   */
  delete(url, config = {}) {
    return this.request({
      ...config,
      url,
      method: 'DELETE'
    })
  }

  /**
   * PATCH请求
   * @param {string} url - 请求URL
   * @param {*} data - 请求数据
   * @param {Object} config - 请求配置
   * @returns {Promise} 请求Promise
   */
  patch(url, data, config = {}) {
    return this.request({
      ...config,
      url,
      method: 'PATCH',
      data
    })
  }

  /**
   * 带熔断机制的GET请求
   * @param {string} url - 请求URL
   * @param {Object} params - 查询参数
   * @param {Object} config - 请求配置 config.code - 熔断机制的唯一标识
   * @returns {Promise} 请求Promise
   */
  getWithCode(url, params = {}, config = {}) {
    return this.request({
      ...config,
      params: JSON.parse(JSON.stringify(params)),
      url,
      method: 'GET'
    })
  }

  /**
   * 带熔断机制的POST请求
   * @param {string} url - 请求URL
   * @param {*} data - 请求数据
   * @param {Object} config - 请求配置  config.code - 熔断机制的唯一标识
   * @returns {Promise} 请求Promise
   */
  postWithCode(url, data, config = {}) {
    return this.request({
      ...config,
      url,
      method: 'POST',
      data
    })
  }
}

// 创建默认实例
const httpClient = new HttpClient({
  baseURL: '/valuation',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 添加请求拦截器示例
httpClient.addRequestInterceptor(
  config => {
    // 在发送请求之前做些什么
    const token = localStorage.getItem('token')
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`
      }
    }
    return config
  },
  error => {
    // 对请求错误做些什么
    return Promise.reject(error)
  }
)

// 添加响应拦截器示例
httpClient.addResponseInterceptor(
  response => {
    // 对响应数据做点什么
    return response
  },
  error => {
    // 对响应错误做点什么
    if (error.response && error.response.status === 401) {
      // 处理未授权错误
      localStorage.removeItem('token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// 导出实例和类
export { HttpClient }
export default httpClient
